/**
 * Static option lists for Traveler's AI Preferences —
 * mirrors iOS OnboardingPreferenceOptions / CuisineTypes / SightseeingStyle / travel style tags.
 */

export const ACCOMMODATION_TYPES = [
  'boutique',
  'luxury',
  'budget',
  'chain',
  'resort',
  'rental home',
] as const;

export const DIETARY_PREFERENCES = [
  'vegetarian',
  'vegan',
  'gluten-free',
  'dairy-free',
  'kosher',
  'halal',
  'low-carb',
  'paleo',
] as const;

export const TRAVEL_PACES = ['relaxed', 'moderate', 'fast-paced'] as const;
export const DEFAULT_TRAVEL_PACE = 'moderate';

export const TIME_SLOTS = [
  'morning',
  'lunch',
  'afternoon',
  'evening',
  'night',
] as const;

export const TRAVEL_STYLE_TAGS = [
  'Solo',
  'Couple',
  'Family',
  'Group',
  'Budget',
  'Moderate',
  'Luxury',
  'Adventure',
  'Cultural',
  'Relaxation',
  'Foodie',
] as const;

export const CUISINE_TYPES = [
  'Authentic Local Cuisine',
  'American',
  'Burger',
  'Sandwiches',
  'Italian',
  'Pizza',
  'Pasta',
  'French',
  'Mexican',
  'Latin American',
  'Japanese',
  'Sushi',
  'Teppanyaki/Hibachi',
  'Chinese',
  'Thai',
  'Indian',
  'Middle Eastern',
  'Mediterranean',
  'Korean',
  'Vietnamese',
  'Greek',
  'Spanish',
  'Steakhouse',
  'Seafood',
  'Fusion',
  'Café/Bakery',
  'Dessert',
] as const;

export type CuisinePreferenceValue = 'love' | 'like' | 'avoid';

export const SIGHTSEEING_STYLES = [
  { value: 'no_preference', labelKey: 'sightseeingNoPreference' as const },
  { value: 'independent', labelKey: 'sightseeingIndependent' as const },
  { value: 'self_guided', labelKey: 'sightseeingSelfGuided' as const },
  { value: 'small_group', labelKey: 'sightseeingSmallGroup' as const },
  { value: 'large_group', labelKey: 'sightseeingLargeGroup' as const },
  { value: 'private_guide', labelKey: 'sightseeingPrivateGuide' as const },
] as const;

/** iOS AccommodationBudget.steps */
export const ACCOMMODATION_BUDGET_STEPS = [
  ...Array.from({ length: 15 }, (_, i) => 50 + i * 25),
  450,
  500,
  600,
  750,
] as const;

export const ACCOMMODATION_BUDGET_DEFAULT = 150;
export const ACCOMMODATION_BUDGET_MAX = 750;

export function normalizeAccommodationBudget(value: number | null | undefined): number {
  if (value == null || !Number.isFinite(value)) return ACCOMMODATION_BUDGET_DEFAULT;
  const rounded = Math.round(value);
  let best = ACCOMMODATION_BUDGET_STEPS[0];
  let bestDist = Math.abs(best - rounded);
  for (const step of ACCOMMODATION_BUDGET_STEPS) {
    const dist = Math.abs(step - rounded);
    if (dist < bestDist) {
      best = step;
      bestDist = dist;
    }
  }
  return best;
}

export function accommodationBudgetIndex(value: number): number {
  const normalized = normalizeAccommodationBudget(value);
  const idx = ACCOMMODATION_BUDGET_STEPS.indexOf(
    normalized as (typeof ACCOMMODATION_BUDGET_STEPS)[number]
  );
  return idx >= 0 ? idx : ACCOMMODATION_BUDGET_STEPS.indexOf(ACCOMMODATION_BUDGET_DEFAULT);
}

/** iOS TripTaskListDefaults.seedTitles */
export const DEFAULT_TRIP_TASK_SEED_TITLES = [
  'Passport valid',
  'Is Visa required',
  'Check with Airline',
  'Pre-reserve Internet access',
  'Book ride to airport',
  'Exchange Currency',
  'Power Adapter',
  'Phone charger and cords',
  'Hat',
  'Towel Keeper',
] as const;

export const ACTIVITY_INTEREST_MIN = 3;
export const ACTIVITY_INTEREST_MAX = 5;

export function isValidActivityInterestCount(count: number): boolean {
  return count >= ACTIVITY_INTEREST_MIN && count <= ACTIVITY_INTEREST_MAX;
}
